import styles from './Skeleton.module.css';

type SkeletonVariant = 'text' | 'circle' | 'rectangle' | 'card';

interface SkeletonProps {
  /** Visual variant of the skeleton placeholder. */
  variant?: SkeletonVariant;
  /** Width (CSS value). Defaults to 100% for text/rectangle/card, auto for circle. */
  width?: string;
  /** Height (CSS value). Defaults vary by variant. */
  height?: string;
  /** Number of skeleton lines to render (only for text variant). */
  lines?: number;
  /** Additional CSS class name. */
  className?: string;
}

/**
 * Skeleton loading placeholder component.
 *
 * Provides visual loading states for cards, tables, text blocks, and avatars.
 * Uses pulse animation for perceived performance.
 *
 * Variants:
 * - text: horizontal lines simulating text content
 * - circle: circular avatar placeholder
 * - rectangle: generic rectangular placeholder
 * - card: card-shaped placeholder with padding
 */
export function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 3,
  className,
}: SkeletonProps) {
  const classNames = [
    styles.skeleton,
    styles[variant],
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (variant === 'text' && lines > 1) {
    return (
      <div
        className={styles.textGroup}
        style={{ width }}
        role="status"
        aria-busy="true"
        aria-label="Carregando conteudo"
      >
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className={classNames}
            style={{
              height: height ?? '1rem',
              width: index === lines - 1 ? '75%' : '100%',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={classNames}
      style={{ width, height }}
      role="status"
      aria-busy="true"
      aria-label="Carregando conteudo"
    />
  );
}
